import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Toast, ToastType, ToastPosition, ToastStore } from '../types/toast-types';

const DEFAULT_TOAST_DURATION = 2000;

let toastIdCounter = 0;
const generateToastId = () => `toast-${Date.now()}-${++toastIdCounter}`;

/**
 * Toast Store
 * ai-companion-next 스타일의 Zustand 기반 토스트 상태 관리
 */
export const useToastStore = create<ToastStore>()(
  devtools(
    (set, get) => ({
      // State
      toasts: [],

      // Actions
      showToast: ({ type, message, position = 'bottom', duration }) => {
        const id = generateToastId();
        console.log('🍞 [TOAST-STORE] showToast 호출:', { id, type, message: message?.substring(0, 30), position, duration });

        const newToast: Toast = {
          id,
          message,
          type,
          position,
          isExiting: false,
        };

        const actualDuration = duration ?? DEFAULT_TOAST_DURATION;

        set((state) => {
          console.log('🍞 [TOAST-STORE] 토스트 추가, 현재 개수:', state.toasts.length, '-> ', state.toasts.length + 1);
          return {
            toasts: [...state.toasts, newToast],
          };
        });

        // 자동 해제 (duration이 0이면 수동 해제)
        if (actualDuration > 0) {
          setTimeout(() => {
            get().hideToast(id);
          }, actualDuration);
        }

        return id;
      },

      hideToast: (id) => {
        // isExiting을 true로 설정하여 퇴장 애니메이션 트리거
        set((state) => ({
          toasts: state.toasts.map((t) =>
            t.id === id ? { ...t, isExiting: true } : t
          ),
        }));

        // 250ms 후 실제 제거 (애니메이션 시간)
        setTimeout(() => {
          set((state) => ({
            toasts: state.toasts.filter((toast) => toast.id !== id),
          }));
        }, 250);
      },

      updateToastMessage: (id, message) => {
        set((state) => ({
          toasts: state.toasts.map((t) =>
            t.id === id ? { ...t, message } : t
          ),
        }));
      },

      clearAllToasts: () => {
        const toasts = get().toasts;
        toasts.forEach((toast) => get().hideToast(toast.id));
      },
    }),
    { name: 'custom-avatar-toast-store' }
  )
);

/**
 * 스토어 외부에서 사용할 수 있는 토스트 액션
 * 컴포넌트 외부에서 토스트를 표시할 때 사용
 */
export const toastActions = {
  show: (params: {
    type: ToastType;
    message: string;
    position?: ToastPosition;
    duration?: number;
  }) => useToastStore.getState().showToast(params),

  hide: (id: string) => useToastStore.getState().hideToast(id),

  update: (id: string, message: string) =>
    useToastStore.getState().updateToastMessage(id, message),

  clearAll: () => useToastStore.getState().clearAllToasts(),
};
